/**
 * Transport-planning view ("Quy hoạch giao thông").
 *  - Planned road network: derived per tile by a Web Worker from the official zone plans
 *    (project boundary minus land-use cells; "Đất giao thông" / "Đất bãi đỗ xe" cells
 *    recoloured), general plan where no zone plan exists. Served through the qhgt:// protocol.
 *  - Existing rail, metro and roads under construction from OpenStreetMap (OpenMapTiles schema
 *    of the OpenFreeMap basemap), drawn above it.
 */
import { api } from './api.js';

export const GT = 'gt';
const EMPTY_PNG = Uint8Array.from(atob('iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAAC0lEQVR42mNgAAIAAAUAAen63NgAAAAASUVORK5CYII='), (c) => c.charCodeAt(0)).buffer;

let worker = null;
let initP = null;
let seq = 0;
const waiting = new Map();

function ensureWorker() {
  if (!initP) {
    initP = api.transportMasks().then((masks) => {
      worker = new Worker('/assets/b/transport-tiles-4XDPPLCM.js', { type: 'module' });
      worker.onmessage = (e) => {
        const w = waiting.get(e.data.id);
        if (!w) return;
        waiting.delete(e.data.id);
        if (e.data.error) w.reject(new Error(e.data.error));
        else w.resolve(e.data.data || EMPTY_PNG.slice(0));
      };
      worker.postMessage({ type: 'init', masks, origin: location.origin });
    }).catch((e) => { initP = null; throw e; });
  }
  return initP;
}

let registered = false;
export function registerTransportProtocol(maplibregl) {
  if (registered || !maplibregl?.addProtocol) return;
  registered = true;
  maplibregl.addProtocol('qhgt', async (params, ac) => {
    const m = /^qhgt:\/\/([ld])\/(\d+)\/(\d+)\/(\d+)/.exec(params.url);
    if (!m) throw new Error('bad transport tile url');
    await ensureWorker();
    const id = ++seq;
    const data = await new Promise((resolve, reject) => {
      waiting.set(id, { resolve, reject });
      worker.postMessage({ type: 'tile', id, theme: m[1], z: +m[2], x: +m[3], y: +m[4] });
      ac?.signal?.addEventListener('abort', () => {
        if (!waiting.delete(id)) return;
        worker.postMessage({ type: 'abort', id });
        reject(new DOMException('Aborted', 'AbortError'));
      });
    });
    return { data };
  });
}

/** Load the boundary masks early (e.g. when the view is about to be shown). */
export const prepareTransport = () => ensureWorker().catch(() => {});

export function transportTiles(theme) {
  return `qhgt://${theme === 'dark' ? 'd' : 'l'}/{z}/{x}/{y}`;
}

/* ---------------- OpenStreetMap overlay (rail, metro, construction, stations) ---------------- */

export const OMT_URL = 'https://tiles.openfreemap.org/planet';
const NAME = ['coalesce', ['get', 'name:vi'], ['get', 'name'], ['get', 'name:latin']];
const CONSTRUCTION = ['motorway_construction', 'trunk_construction', 'primary_construction', 'secondary_construction', 'tertiary_construction', 'minor_construction'];

export function transportColors(theme, imagery) {
  const dark = theme === 'dark' || imagery;
  return {
    rail: dark ? '#e2e8f0' : '#334155',
    railGap: dark ? '#0f172a' : '#ffffff',
    metro: dark ? '#f472b6' : '#be185d',
    casing: dark ? 'rgba(2, 6, 23, 0.85)' : 'rgba(255, 255, 255, 0.95)',
    construction: dark ? '#fb923c' : '#c2410c',
    text: dark ? '#f8fafc' : '#0f172a',
    halo: dark ? 'rgba(2, 6, 23, 0.9)' : 'rgba(255, 255, 255, 0.95)',
  };
}

/** Line / symbol layers on the OpenMapTiles source; ids prefixed qh-gt-. */
export function transportLayers(source, c) {
  const w = (a, b) => ['interpolate', ['linear'], ['zoom'], 10, a, 17, b];
  return [
    { id: 'qh-gt-constr', type: 'line', source, 'source-layer': 'transportation', minzoom: 11,
      filter: ['in', ['get', 'class'], ['literal', CONSTRUCTION]],
      layout: { 'line-join': 'round', 'line-cap': 'butt' },
      paint: { 'line-color': c.construction, 'line-width': w(1.2, 3.2), 'line-dasharray': [2, 1.6], 'line-opacity': 0.95 } },
    { id: 'qh-gt-rail-case', type: 'line', source, 'source-layer': 'transportation',
      filter: ['all', ['==', ['get', 'class'], 'rail'], ['in', ['get', 'subclass'], ['literal', ['rail', 'narrow_gauge']]]],
      paint: { 'line-color': c.casing, 'line-width': w(3, 7) } },
    { id: 'qh-gt-rail', type: 'line', source, 'source-layer': 'transportation',
      filter: ['all', ['==', ['get', 'class'], 'rail'], ['in', ['get', 'subclass'], ['literal', ['rail', 'narrow_gauge']]]],
      paint: { 'line-color': c.rail, 'line-width': w(1.6, 4) } },
    { id: 'qh-gt-rail-dash', type: 'line', source, 'source-layer': 'transportation', minzoom: 12,
      filter: ['all', ['==', ['get', 'class'], 'rail'], ['in', ['get', 'subclass'], ['literal', ['rail', 'narrow_gauge']]]],
      paint: { 'line-color': c.railGap, 'line-width': w(0.6, 2), 'line-dasharray': [3, 3] } },
    { id: 'qh-gt-metro-case', type: 'line', source, 'source-layer': 'transportation',
      filter: ['any', ['==', ['get', 'class'], 'transit'], ['in', ['get', 'subclass'], ['literal', ['subway', 'light_rail', 'monorail']]]],
      layout: { 'line-join': 'round', 'line-cap': 'round' },
      paint: { 'line-color': c.casing, 'line-width': w(4, 9) } },
    { id: 'qh-gt-metro', type: 'line', source, 'source-layer': 'transportation',
      filter: ['any', ['==', ['get', 'class'], 'transit'], ['in', ['get', 'subclass'], ['literal', ['subway', 'light_rail', 'monorail']]]],
      layout: { 'line-join': 'round', 'line-cap': 'round' },
      paint: { 'line-color': c.metro, 'line-width': w(2.2, 5.5) } },
  ];
}

/** Station dots + names (placed above the basemap labels). */
export function transportSymbols(source, c) {
  return [
    { id: 'qh-gt-station', type: 'circle', source, 'source-layer': 'poi', minzoom: 12,
      filter: ['all', ['==', ['get', 'class'], 'railway'], ['in', ['get', 'subclass'], ['literal', ['station', 'halt']]]],
      paint: { 'circle-radius': ['interpolate', ['linear'], ['zoom'], 12, 3.5, 17, 7], 'circle-color': '#ffffff', 'circle-stroke-color': c.metro, 'circle-stroke-width': ['interpolate', ['linear'], ['zoom'], 12, 2, 17, 3] } },
    { id: 'qh-gt-station-label', type: 'symbol', source, 'source-layer': 'poi', minzoom: 13.5,
      filter: ['all', ['==', ['get', 'class'], 'railway'], ['in', ['get', 'subclass'], ['literal', ['station', 'halt']]]],
      layout: { 'text-field': NAME, 'text-font': ['Noto Sans Bold'], 'text-size': 11.5, 'text-offset': [0, 1.1], 'text-anchor': 'top', 'text-max-width': 8, 'text-optional': true },
      paint: { 'text-color': c.text, 'text-halo-color': c.halo, 'text-halo-width': 1.6 } },
  ];
}
